import { Blac, Cubit } from "blac-next";
import PushNotificationsBloc from "src/hybrid/components/PushNotificationsControls";
import createTrackEvent from "src/lib/createTrackEvent";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import StorageBloc, {
  StorageController
} from "src/state/StorageBloc/StorageBloc";
import { tracker } from "src/state/state";
import ConversationThreadCubit from "src/ui/components/LeapChat/state/ConversationThreadCubit";

const DISMISS_STORAGE_KEY = StorageBloc.keys.pushPromptDismissedAt;
const DISMISS_COOLDOWN_MS = 5 * 24 * 60 * 60 * 1000;

export interface RequestPushPermissionsState {
  show: boolean;
}

export default class RequestPushPermissionsCubit extends Cubit<RequestPushPermissionsState> {
  static keepAlive = true;

  constructor() {
    super({ show: false });
  }

  init = (): void => {
    this.hidePrompt();
    const appView = Blac.getBloc(AppViewCubit);
    appView.setOnEscalationOccurredCallback(() => this.evaluate());
  };

  evaluate = (): void => {
    const pushBloc = Blac.getBloc(PushNotificationsBloc);
    const appView = Blac.getBloc(AppViewCubit);
    const { escalationOccurred = false } = appView.state;

    const thread =
      Blac.getAllBlocs(ConversationThreadCubit).find(
        (t) => !!t.conversationId
      ) ?? null;
    const messages = thread?.state.messages ?? [];
    const messagesLength = messages.length;
    const lastMessage = messages[messages.length - 1];
    const lastMessageIsFromUser = lastMessage?.role === "user";

    // eslint-disable-next-line no-console
    const log = (reason: string) =>
      console.log("[PushPrompt]", reason, {
        isAvailable: pushBloc.isAvailable,
        permission: pushBloc.state.permissions?.receive,
        messagesLength,
        lastMessageIsFromUser,
        escalationOccurred,
        dismissedRecently: this.isDismissedRecently(),
        isInitializing: thread?.state.isInitializing
      });

    if (!pushBloc.isAvailable) {
      log("skip: push not available");
      this.hidePrompt();
      return;
    }

    if (pushBloc.state.permissions?.receive === "granted") {
      log("skip: permission already granted");
      this.hidePrompt();
      return;
    }

    if (!thread?.conversationId || thread.state.isInitializing) {
      log("skip: thread not ready");
      this.hidePrompt();
      return;
    }

    if (messagesLength === 0) {
      log("skip: no messages");
      this.hidePrompt();
      return;
    }

    if (this.isDismissedRecently()) {
      log("skip: dismissed recently");
      this.hidePrompt();
      return;
    }

    const shouldTrigger =
      lastMessageIsFromUser || escalationOccurred || this.state.show;
    if (!shouldTrigger) {
      log(
        "skip: no trigger (need lastMessageIsFromUser or escalationOccurred)"
      );
      return;
    }

    log("show: showing prompt");
    this.patch({ show: true });
    if (escalationOccurred) {
      appView.setEscalationOccurred(false);
    }
    this.trackInitiated();
  };

  handlePromptNow = (): void => {
    tracker.track(createTrackEvent("Push Notification Access - Prompt"));
    const pushBloc = Blac.getBloc(PushNotificationsBloc);
    void pushBloc.requestPermissions({ goToSettings: true });
  };

  handleDismiss = (): void => {
    this.hidePrompt();
    StorageController.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
  };

  private hidePrompt(): void {
    if (this.state.show) {
      this.patch({ show: false });
    }
  }

  private isDismissedRecently(): boolean {
    const raw = StorageController.getItem(DISMISS_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const dismissedAt = parseInt(raw, 10);
    if (Number.isNaN(dismissedAt)) {
      return false;
    }
    return Date.now() - dismissedAt < DISMISS_COOLDOWN_MS;
  }

  private trackInitiated(): void {
    tracker.track(createTrackEvent("Push Notification Access - Initiated"));
  }
}
